import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useRouteMatch, withRouter } from 'react-router';
import { Layout, Row, Col, Card, message, Alert, Modal, Drawer, Button, Popconfirm } from 'antd';
import posthog from 'posthog-js';
import { InvoiceFormBase, createBuyerInvoiceConfig } from '../InvoiceFormBase';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import InvoiceUploadandParse, {
	InvoiceLoadingSkeletonAnimated,
} from '../invoice_uploader/InvoiceUploadandParse';
import {
	createInvoiceForSupplierBasedOnWorkOrderForBuyer,
	createInvoiceForSupplierBasedOnWorkOrderForSupplier,
	createInvoiceByInternalTech,
	invoicesRestCrudThunksForBuyer,
	invoicesRestCrudThunksForSupplier,
	fetchTaxLineItemsForBuyer,
	fetchTaxLineItemsForSupplier,
	getInvoiceNumber,
	getInvoiceNumberBuyer,
} from '../../thunks/invoices_thunks';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import {
	applyParsedInvoiceToForm,
	buildInvoicePayloadFromFormValues,
	compareParsedWithFinal,
	getCurrencyMismatch,
} from '../../utils/InvoiceUtils';
import { getEntityById, getCurrency, nullSafeGet } from '../../utils/DataAccessUtils';
import ClearErrors from '../clear_errors/ClearErrors';
import { INVOICES_CRUD_ACTION_CREATORS } from '../../actions/invoices_actions';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { withUserType } from '../withUserType';
import ResizableSplitPanel from '../common/ResizableSplitPanel';
import {
	proposalsRestCrudThunksForBuyer,
	proposalsRestCrudThunksForSupplier,
} from '../../thunks/proposals_thunks';
import { BillToInfoBanner } from '../bill_to_info_banner/BillToInfoBanner';

const queryString = require('qs');
const { Content } = Layout;

const NewBuyerInvoicePageV2: FC<any> = ({
	createInvoiceWithWorkOrderId,
	createInvoice,
	updateInvoice,
	fetchTaxLineItems,
	checkInvoiceNumber,
	userType,
	currentUser,
	workOrder,
	creating,
	updating,
	createErrors,
	updateErrors,
	getQuotes,
	companyConfig,
}): React.ReactElement => {
	const [invoiceFormData, setInvoiceFormData] = useState<any>({});
	const [invoiceFormDataLoading, setInvoiceFormDataLoading] = useState(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [formKey, setFormKey] = useState(0);
	// Bumped on user-initiated "start over" events (delete, reset, error,
	// currency mismatch). NOT bumped on parse-complete — remounting the
	// uploader mid-S3 would orphan the in-flight upload, and we'd lose the
	// final invoicePDFLink.
	const [uploaderKey, setUploaderKey] = useState(0);
	const [parsedDiscount, setParsedDiscount] = useState(null);
	const [parsedFormDataSnapshot, setParsedFormDataSnapshot] = useState<any>(null);
	const [parsingLoading, setParsingLoading] = useState(false);
	const location = useLocation();
	const history = useHistory();
	const match = useRouteMatch();
	const [showDrawer, setShowDrawer] = useState(false);
	const [disableCosts, setDisableCosts] = useState(false);
	const [hasQuote, setHasQuote] = useState(false);

	// Fires when the S3 upload completes. invoicePDFLink is only ever set from
	// here, so it's always a real URL — never the local base64 preview.
	const handleS3UrlReady = useCallback((s3Url: string) => {
		setInvoiceFormData((prev) => ({ ...prev, invoicePDFLink: s3Url }));
		setPdfUrl(s3Url);
	}, []);

	// Fires after S3 retries are exhausted. Clear any stale link from a prior
	// upload, reset the dragger so the user can pick a new file, and surface
	// the failure as a toast.
	const handleS3UploadError = useCallback(() => {
		message.error('Failed to upload invoice PDF. Please try uploading again.');
		setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
		setUploaderKey((prev) => prev + 1);
	}, []);

	const workOrderId = useMemo(() => {
		let searchString = location.search;
		if (searchString[0] === '?') {
			searchString = searchString.slice(1);
		}

		const queryParams = queryString.parse(searchString);
		return queryParams.workOrderId || match.params.id;
	}, [location.search, match.params.id]);

	useEffect(() => {
		if (nullSafeGet('id', workOrder)) {
			getQuotes(
				{
					workOrderId: nullSafeGet('id', workOrder),
					buyerFacilityId: workOrder.buyerFacilityId,
				},
				'workOrderAssociatedQuotes'
			).then((proposals) => {
				if (proposals && proposals.length > 0) {
					setHasQuote(true);
				}
			});
		}
	}, []);

	const currency = useMemo(() => {
		if (!!hasQuote) {
			return getCurrency({ workOrder });
		}
		return getCurrency({ supplier: nullSafeGet('supplierFacility', workOrder) });
	}, [hasQuote, workOrder]);

	useEffect(() => {
		if (workOrderId) {
			setInvoiceFormData((prevFormData) => {
				if (prevFormData.workOrderId !== workOrderId) {
					return {
						...prevFormData,
						workOrderId: workOrderId,

						externalWorkOrderId: prevFormData.externalWorkOrderId || String(workOrderId),
						supplierFacilityId:
							workOrder && workOrder.supplierFacilityId
								? workOrder.supplierFacilityId
								: prevFormData.supplierFacilityId,
					};
				}

				if (!prevFormData.externalWorkOrderId) {
					return {
						...prevFormData,
						externalWorkOrderId: String(workOrderId),
					};
				}
				return prevFormData;
			});
		}
	}, [workOrderId, workOrder]);

	const handleDeletePdf = useCallback(() => {
		if (parsedFormDataSnapshot) {
			posthog.capture('invoice_pdf_deleted_after_parsing', {
				user_type: userType,
			});
		}
		setPdfUrl(null);
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);
		setShowDrawer(false);

		setInvoiceFormData((prevFormData) => {
			if (prevFormData) {
				return { ...prevFormData, invoicePDFLink: undefined };
			}
			return prevFormData;
		});
	}, [parsedFormDataSnapshot, userType]);

	const handleBackConfirm = useCallback(() => {
		handleDeletePdf();
	}, [handleDeletePdf]);

	const handleReset = useCallback(() => {
		setParsedDiscount(null);
		setParsedFormDataSnapshot(null);
		setParsingLoading(false);
		setDisableCosts(false);

		// Reset form data to initial state with only workOrderId
		setInvoiceFormData({
			workOrderId: workOrderId,
			externalWorkOrderId: String(workOrderId),
			supplierFacilityId:
				workOrder && workOrder.supplierFacilityId ? workOrder.supplierFacilityId : undefined,
		});

		// Increment form key to force re-render
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);

		// Track reset event
		posthog.capture('invoice_form_reset', {
			user_type: userType,
			had_parsed_data: !!parsedFormDataSnapshot,
		});
	}, [workOrderId, workOrder, userType, parsedFormDataSnapshot]);

	const handleFileSelected = useCallback((fileData) => {
		// Show PDF immediately when file is selected
		if (fileData.fileUrl) {
			setPdfUrl(fileData.fileUrl);
			setParsingLoading(true);
			setShowDrawer(true);
		} else {
			console.error('handleFileSelected: fileUrl is undefined');
		}
	}, []);

	const handleUploadError = useCallback((error = {}) => {
		message.error('Error parsing invoice. Please try again.');
		setParsingLoading(false);
		setShowDrawer(false);
		setFormKey((prev) => prev + 1);
		setUploaderKey((prev) => prev + 1);
		// Drop any link from a prior upload so a follow-up can't submit with a stale PDF.
		setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
		posthog.capture('invoice_pdf_parse_error', {
			user_type: userType,
			error: error.message,
		});
	}, []);

	const handleParsingStart = useCallback(() => {
		setParsingLoading(true);
	}, []);

	const handleParsingComplete = useCallback(() => {
		setParsingLoading(false);
	}, []);

	const handleUploadSuccess = useCallback(
		(data) => {
			if (!data.parsedData) {
				return;
			}

			const mismatch = getCurrencyMismatch(data.parsedData, currency);
			if (mismatch) {
				setPdfUrl(null);
				setParsingLoading(false);
				setShowDrawer(false);
				setFormKey((prev) => prev + 1);
				setUploaderKey((prev) => prev + 1);
				// Drop the wrong-currency PDF's link so it can't get submitted later.
				setInvoiceFormData((prev) => (prev ? { ...prev, invoicePDFLink: undefined } : prev));
				Modal.confirm({
					title: 'Currency Mismatch',
					content: `The uploaded invoice is in ${mismatch.parsedCurrency}, but your account is configured to use ${mismatch.systemCurrency}.`,
					okText: 'Create from Scratch',
					cancelText: 'Cancel',
					onOk: () => {
						history.push(
							userType === ROLE_TYPES.BUYER
								? `/${userType}/workOrders/detail/${workOrderId}/invoices/new`
								: `/supplier/workOrders/detail/${workOrderId}/invoices/newForSupplier`
						);
						posthog.capture('invoice_pdf_currency_mismatch_create_from_scratch', {
							user_type: userType,
							parsed_currency: mismatch.parsedCurrency,
							system_currency: mismatch.systemCurrency,
						});
					},
					width: '50vw',
					centered: true,
				});
				return;
			}

			applyParsedInvoiceToForm({
				parsedData: data.parsedData,
				workOrder,
				userType,
				setInvoiceFormData,
				setParsedFormDataSnapshot,
				setParsedDiscount,
				setDisableCosts,
			});
			setFormKey((prev) => prev + 1);
			setShowDrawer(true);
		},
		[workOrder, userType, currency, history, workOrderId]
	);

	const onSuccess = useCallback(
		(rec) => {
			history.replace(
				getLinkWIthBackLinkParams(
					{ ...location, pathname: `/${userType}/workOrders/detail/${workOrderId}/invoices` },
					'Back to Work Order',
					`/${userType}/invoices/detail/${rec.id}`
				)
			);
		},
		[history, userType, location, workOrderId]
	);

	const handleSubmit = useCallback(
		async (values, formInstance, scope) => {
			const currentInvoicePDFLink = nullSafeGet('invoicePDFLink', invoiceFormData);
			if (!currentInvoicePDFLink) {
				message.error('Invoice PDF is still uploading. Please wait a moment and try again.');
				return;
			}

			const isUpdate = values.id ? true : false;
			const submitAction = values.id ? updateInvoice : createInvoice;

			const allFormValues = formInstance.getFieldsValue(true);
			const formValues = { ...allFormValues, ...values };

			const invoice = buildInvoicePayloadFromFormValues(formValues, scope, {
				workOrderId,
				currentUser,
				role: 'buyer',
				baseMeta: {
					invoicePDFLink: currentInvoicePDFLink,
					isDeleted: nullSafeGet('isDeleted', invoiceFormData),
					buyerFacilityId:
						nullSafeGet('buyerFacilityId', formValues) || nullSafeGet('facility.id', currentUser),
					supplierFacilityId: nullSafeGet('supplierFacilityId', invoiceFormData),
				},
				status: formValues.status || 'pending',
			});

			if (formValues.signatureName) {
				invoice.aiParseMismatchSignedByEmail = currentUser.email;
			}

			try {
				const finalInvoice = isUpdate ? { ...invoiceFormData, ...invoice } : invoice;

				if (parsedFormDataSnapshot) {
					const changeMetrics = compareParsedWithFinal(parsedFormDataSnapshot, finalInvoice);
					const hadEdits =
						nullSafeGet('total_fields_changed', changeMetrics) > 0 ||
						nullSafeGet('total_line_items_changed', changeMetrics) > 0;

					if (isUpdate) {
						posthog.capture('invoice_updated_from_parsed_pdf', {
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						});
					} else {
						posthog.capture('invoice_created_from_parsed_pdf', {
							user_type: userType,
							was_parsed: true,
							user_changed_fields: hadEdits,
							...changeMetrics,
						});
					}
					finalInvoice.isAiParsed = true;
					finalInvoice.isAiParsedEdited = hadEdits;
				}
				const record = await submitAction(finalInvoice);

				if (onSuccess) {
					onSuccess(record);
				}
			} catch (err) {
				if (
					typeof err === 'string' &&
					err.indexOf('Invoice(s) are already associated with this work order') === 0
				) {
					message.error(err);
					history.goBack();
				}
				throw err;
			}
		},
		[
			updateInvoice,
			createInvoice,
			invoiceFormData,
			currentUser,
			onSuccess,
			history,
			parsedFormDataSnapshot,
			userType,
			workOrderId,
		]
	);

	const formConfig = useMemo(() => {
		if (!invoiceFormData || !workOrder || !currentUser) {
			return null;
		}

		return createBuyerInvoiceConfig(
			invoiceFormData,
			workOrder,
			currentUser,
			history,
			handleSubmit,
			{
				creating,
				updating,
				createErrors,
				updateErrors,
				parsedDataSnapshot: parsedFormDataSnapshot,
				disableCosts: disableCosts,
				onReset: handleReset,
				fetchTaxLineItems,
				currency,
				checkInvoiceNumber,
			}
		);
	}, [
		invoiceFormData,
		workOrder,
		currentUser,
		history,
		handleSubmit,
		creating,
		updating,
		createErrors,
		updateErrors,
		parsedFormDataSnapshot,
		disableCosts,
		handleReset,
		fetchTaxLineItems,
		currency,
		checkInvoiceNumber,
	]);

	if (invoiceFormDataLoading || !formConfig) {
		return <PageLoadingPlaceholder />;
	}
	return (
		<Content>
			<BillToInfoBanner
				location={nullSafeGet('location', workOrder)}
				workOrder={workOrder}
				userType={userType}
				companyConfig={companyConfig}
				currentUser={currentUser}
			/>
			<InvoiceUploadandParse
				key={uploaderKey}
				handleUploadSuccess={handleUploadSuccess}
				onFileSelected={handleFileSelected}
				onParsingStart={handleParsingStart}
				onParsingComplete={handleParsingComplete}
				onS3UrlReady={handleS3UrlReady}
				onS3UploadError={handleS3UploadError}
				uploaderType="dragger"
				onError={handleUploadError}
			/>
			<Drawer
				width="100vw"
				placement="right"
				visible={showDrawer}
				bodyStyle={{ height: '100vh', padding: 0, overflow: 'hidden' }}
				style={{ top: 0, height: '100vh' }}
				className="fullscreen-drawer"
				// headerStyle={{ display: 'none' }}
				closeIcon={
					<Popconfirm
						title="Are you sure you want to go back? Any unsaved changes will be lost."
						onConfirm={handleBackConfirm}
						okText="Yes"
						cancelText="No"
					>
						<Button type="ghost" icon={<ArrowLeftOutlined />}>
							Back
						</Button>
					</Popconfirm>
				}
				title={<div className="h3medium">Create Invoice</div>}
			>
				<Row style={{ height: '100%' }}>
					<ClearErrors actionCreators={INVOICES_CRUD_ACTION_CREATORS} />
					<Col span={24} style={{ height: '100%' }}>
						<ResizableSplitPanel
							leftPanel={
								<div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
									<OWPdfViewer
										file={pdfUrl}
										fileName="Invoice.pdf"
										showDelete={true}
										onDelete={handleDeletePdf}
									/>
								</div>
							}
							rightPanel={
								<div>
									{parsingLoading ? (
										<InvoiceLoadingSkeletonAnimated />
									) : (
										<InvoiceFormBase
											key={formKey}
											config={formConfig}
											handleCancel={handleDeletePdf}
										/>
									)}
								</div>
							}
							rightPanelClassName="flex-1 min-h-0 overflow-y-auto p-6 pt-7"
						/>
					</Col>
				</Row>
			</Drawer>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	creating: state.invoices.creating,
	updating: state.invoices.updating,
	createErrors: state.invoices.createErrors,
	updateErrors: state.invoices.updateErrors,
	companyConfig: state.company_config.detail,
});
const mapDispatchToProps = (dispatch, ownProps) => ({
	checkInvoiceNumber: (invoice, supplierFacilityId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? getInvoiceNumber(invoice, supplierFacilityId)
				: getInvoiceNumberBuyer(invoice, supplierFacilityId)
		),
	createInvoiceWithWorkOrderId: (workOrderId, entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? createInvoiceForSupplierBasedOnWorkOrderForSupplier(entity, workOrderId)
				: createInvoiceForSupplierBasedOnWorkOrderForBuyer(entity, workOrderId)
		),
	createInvoice: (invoice) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? createInvoiceByInternalTech(invoice)
				: invoicesRestCrudThunksForBuyer.create(invoice)
		),
	updateInvoice: (invoice) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? invoicesRestCrudThunksForSupplier.update(invoice)
				: invoicesRestCrudThunksForBuyer.update(invoice)
		),
	fetchTaxLineItems: (currencyId: string) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? fetchTaxLineItemsForSupplier(currencyId)
				: fetchTaxLineItemsForBuyer(currencyId)
		),
	getQuotes: (params, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? proposalsRestCrudThunksForSupplier.read(params, targetCollectionName)
				: proposalsRestCrudThunksForBuyer.read(params, targetCollectionName)
		),
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(NewBuyerInvoicePageV2))
);
